import {Helmet} from '@dr.pogodin/react-helmet';
import {Action} from 'platform/components';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';
import {DmsDataGrid, Main, PageTab} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {RentalOrderCalendar} from './(Sections)/RentalOrderCalendar/RentalOrderCalendar';
import {RentalOrderKanban} from './(Sections)/RentalOrderKanban/RentalOrderKanban';

const PREDEFINED_COLUMNS = ['state'] as const;

export function RentalOrderOverview() {
  const navigate = useNavigate();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    sourceSearchParams,
    getColumnData,
  }) => {
    if (isArray(rowId)) {
      return;
    }

    match(actionKey)
      .with('detail', 'detail_newTab', () => {
        const state = getColumnData<{key: RentalOrderResponseBody['state']}>('state')?.value?.key;

        const redirectRoute = match(state)
          .with('draft', 'canceled', 'closed', always(rentalOrderRoutes.vehicleAndCustomer))
          .with('reserved', always(rentalOrderRoutes.rentVehicle))
          .with('rented', 'returned', always(rentalOrderRoutes.returnVehicle))
          .otherwise(always(rentalOrderRoutes.vehicleAndCustomer));

        const queryParams = new URLSearchParams(sourceSearchParams);
        queryParams.set('source', 'rental-order-detail');

        const url = composePath(redirectRoute, {
          params: {id: rowId},
          isSearchParamsPreserved: false,
          queryParams: queryParams.toString(),
        });

        if (actionKey === 'detail_newTab') {
          window.open(url, '_blank');
        } else {
          navigate(url);
        }
      })
      .otherwise(noop);
  };

  const actions: Action[] = [
    {
      type: 'button',
      onClick: () => navigate(rentalOrderRoutes.create),
      variant: 'primary',
      'data-testid': testIds.rentalOrder.overview('create'),
      title: i18n.t('entity.rentalOrder.actions.newRentalOrder'),
    },
  ];

  const tabs: PageTab[] = [
    {
      content: (
        <DmsDataGrid
          data-testid={testIds.rentalOrder.overview('datagrid')}
          gridCode="rental-order"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
        />
      ),
      href: rentalOrderRoutes.overview,
      id: 'overview',
      title: i18n.t('page.rentalOrder.labels.overview'),
    },
    {
      content: <RentalOrderKanban />,
      href: rentalOrderRoutes.overviewKanban,
      id: 'overview-kanban',
      title: i18n.t('page.rentalOrder.labels.overviewKanban'),
    },
    {
      content: <RentalOrderCalendar />,
      href: rentalOrderRoutes.overviewCalendar,
      id: 'overview-calendar',
      title: i18n.t('page.rentalOrder.labels.overviewCalendar'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('page.rentalOrder.labels.overview')} />
      <Main
        isFullHeight
        actions={actions}
        data-testid={testIds.rentalOrder.overview('container')}
        tabs={tabs}
      />
    </>
  );
}
